import { Building2, Check, XCircle, Plus, Link as LinkIcon, Info, Circle } from 'lucide-react';
import './styles/PlatformConfiguration.css';

interface Props {
  nextStep: () => void;
  previousStep: () => void;
}

const HoldingSetup = ({ nextStep, previousStep }: Props) => {
  return (
    <div className="organization-wrapper">
      {/* Header */}
      <div className="holding-header">
        <div className="holding-header-text">
          <h2 className="holding-title">Holding Setup</h2>
          <p className="holding-subtitle">Group Information and subsidiaries</p>
        </div>
      </div>

      {/* Info Banner */}
      <div className="holding-banner">
        <Info />
        <p className="holding-banner-text">
          This setup enables a parent group with multiple subsidiary organizations. Each subsidiary may have its own branches, modules and
          configurations.
        </p>
      </div>

      {/* Main Form */}
      <div className="mt-8 space-y-8">
        {/* Group Information */}
        <div className="holding-fields">
          <div className="holding-field">
            <label className="holding-label">Holding / Group Name</label>

            <input type="text" placeholder="Nova Tech" className="holding-input" />
          </div>

          <div className="holding-field">
            <label className="holding-label">Group Code</label>

            <input type="text" placeholder="e.g., MFN" className="holding-input" />
          </div>
        </div>

        {/*Description */}
        <div className="holding-description">
          <label className="holding-description-label">Description / Industry (optional)</label>

          <input type="text" placeholder="e.g. Healthcare & Technology Group" className="holding-description-input" />
        </div>

        {/* Subsidiary Question */}
        <div className="holding-subsidiary">
          <p className="holding-subsidiary-title">Are any subsidiary organizations already using the system?</p>

          <div className="holding-subsidiary-options">
            {/* Selected */}
            <div className="holding-option active">
              <div className="holding-option-icon">
                <Circle fill="#18B58A" />
              </div>

              <div className="holding-option-content">
                <p className="holding-option-title">No</p>
                <p className="holding-option-description">We'll set up all subsidiaries from scratch</p>
              </div>
            </div>

            {/* Unselected */}
            <div className="holding-option">
              <div className="holding-option-icon">
                <Circle />
              </div>

              <div className="holding-option-content">
                <p className="holding-option-title">No</p>
                <p className="holding-option-description">We'll set up all subsidiaries from scratch</p>
              </div>
            </div>
          </div>
        </div>

        {/* Link Existing Organizations */}
        <div className="holding-link-section">
          <div className="holding-link-header">
            <div className="holding-link-title-row">
              <LinkIcon className="holding-link-icon" />

              <h3 className="holding-link-title">Link Existing Organizations</h3>
            </div>

            <p className="holding-link-description">
              If an organization is already using the system, enter their organization code and linking key to connect them under this
              holding group.
            </p>
          </div>

          {/* Linked Organizations */}
          <div className="space-y-6">
            {/* FIRST NOVA ROW*/}
            <div className="flex items-center justify-between px-4 py-3 bg-white border border-[#eaf9f8] rounded-[10px]">
              <div className="flex items-center gap-2.5">
                <Check className="w-[18px] h-[18px] p-1.5 box-content text-[#14b8a6] bg-[#eafbf8] rounded-md" />
                <p className="font-['Poppins'] text-sm font-semibold text-[#3c3e42] m-0">Nova</p>
              </div>

              <div className="flex items-center gap-2">
                <span className="px-2.5 py-1 bg-[#eaf9f8] text-[#25a88e] rounded-full font-['Poppins'] text-xs font-semibold">Linked</span>
                <XCircle className="w-4 h-4 text-gray-500 cursor-pointer" />
              </div>
            </div>

            {/* SECOND NOVA ROW */}
            <div className="flex items-center justify-between px-4 py-3 bg-white border border-[#eaf9f8] rounded-[10px]">
              <div className="flex items-center gap-2.5">
                <Check className="w-[18px] h-[18px] p-1.5 box-content text-[#14b8a6] bg-[#eafbf8] rounded-md" />
                <p className="font-['Poppins'] text-sm font-semibold text-[#3c3e42] m-0">Nova</p>
              </div>

              <div className="flex items-center gap-2">
                <span className="px-2.5 py-1 bg-[#eaf9f8] text-[#25a88e] rounded-full font-['Poppins'] text-xs font-semibold">Linked</span>
                <XCircle className="w-4 h-4 text-gray-500 cursor-pointer" />
              </div>
            </div>

            {/* BCCA */}
            <div className="holding-organization-card">
              <div className="flex items-center justify-between px-4 py-3 bg-white border border-[#eaf9f8] rounded-[10px]">
                <div className="flex items-center gap-2.5">
                  <Check className="w-[18px] h-[18px] p-1.5 box-content text-[#14b8a6] bg-[#eafbf8] rounded-md" />
                  <p className="font-['Poppins'] text-sm font-semibold text-[#3c3e42] m-0">BCCA</p>
                </div>

                <div className="flex items-center gap-2">
                  <span className="px-2.5 py-1 bg-[#eaf9f8] text-[#25a88e] rounded-full font-['Poppins'] text-xs font-semibold">
                    Linked
                  </span>
                  <XCircle className="w-4 h-4 text-gray-500 cursor-pointer" />
                </div>
              </div>

              {/* Organization Name & Code */}
              <div className="holding-organization-fields">
                <div className="holding-organization-field">
                  <label className="holding-organization-label">Organization Name</label>

                  <input type="text" placeholder="e.g. AI Amal Hospital" className="holding-organization-input" />
                </div>

                <div className="holding-organization-field">
                  <label className="holding-organization-label">Organization Code</label>

                  <input type="text" placeholder="e.g. AAH-001" className="holding-organization-input" />
                </div>
              </div>

              {/* Linking Key */}
              <div className="holding-linking-field">
                <label className="holding-organization-label">Linking Key / Tenant Code</label>

                <input type="text" placeholder="Secure linking key" className="holding-linking-input" />
              </div>
            </div>

            <button className="holding-add-linked">
              <Plus className="holding-add-linked-icon" />
              <span>Add Linked Organization</span>
            </button>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex justify-between mt-12">
        <button
          onClick={previousStep}
          className="border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-lg px-8 h-11 font-medium transition-colors"
        >
          ← Back
        </button>

        <div className="flex gap-3">
          <button className="border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-lg px-8 h-11 font-medium transition-colors">
            Save &amp; Edit
          </button>
          <button
            onClick={nextStep}
            className="bg-[#18B58A] hover:bg-[#14996F] text-white rounded-lg px-10 h-11 font-medium transition-colors flex items-center gap-2"
          >
            Next →
          </button>
        </div>
      </div>
    </div>
  );
};

export default HoldingSetup;
